import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivationQrCode } from '../../components/ActivationQrCode';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { StatusBadge } from '../../components/StatusBadge';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  generateKioskActivationToken,
  getKioskActivationStatus,
  kioskStatusLabel,
} from '../../services/kioskAdminService';
import type { KioskActivationTokenResult } from '../../types/kiosk';
import { copyText } from '../../utils/clipboard';
import { formatDateTime } from '../../utils/dateTime';
import { kioskAdminErrorMessage } from '../../utils/kioskErrors';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'KioskActivationQr'>;

const POLL_MS = 3000;

export function KioskActivationQrScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const { kioskId } = route.params;
  const [payload, setPayload] = useState<KioskActivationTokenResult | null>(
    null,
  );
  const [statusLabel, setStatusLabel] = useState('PENDING');
  const [loading, setLoading] = useState(true);
  const [copyMessage, setCopyMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const themed = useMemo(
    () => ({
      body: { ...typography.body, color: palette.textSecondary },
      token: {
        ...typography.caption,
        color: palette.textPrimary,
        fontFamily: 'monospace',
      },
      success: { ...typography.body, color: palette.success, fontWeight: '600' as const },
      message: { ...typography.caption, color: palette.success },
    }),
    [palette],
  );

  const clearPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const status = await getKioskActivationStatus(kioskId);
      setStatusLabel(kioskStatusLabel(status.status));
      if (status.isActivated) {
        clearPoll();
      }
    } catch {
      // Keep showing the QR; transient poll failures are non-fatal.
    }
  }, [clearPoll, kioskId]);

  const generate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCopyMessage('');
    clearPoll();
    try {
      const result = await generateKioskActivationToken(kioskId);
      setPayload(result);
      setStatusLabel(kioskStatusLabel(result.kiosk.status));
      pollRef.current = setInterval(() => {
        pollStatus().catch(() => undefined);
      }, POLL_MS);
      pollStatus().catch(() => undefined);
    } catch (caught) {
      setPayload(null);
      setError(
        kioskAdminErrorMessage(
          caught,
          'Unable to generate an activation token.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [clearPoll, kioskId, pollStatus]);

  useEffect(() => {
    generate().catch(() => undefined);
    return () => clearPoll();
  }, [clearPoll, generate]);

  async function onCopy() {
    if (!payload?.activationToken) return;
    try {
      const mode = await copyText(payload.activationToken);
      setCopyMessage(
        mode === 'copied'
          ? 'Activation token copied.'
          : 'Share sheet opened with the activation token.',
      );
    } catch {
      setCopyMessage('Unable to copy the activation token.');
    }
  }

  const activated = statusLabel === 'ACTIVE';

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !payload ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Activation QR"
            onBack={navigation.goBack}
          />
          <AppLoader message="Generating activation QR" />
        </AppScreen>
      ) : error && !payload ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title="Activation QR"
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={generate} />
        </AppScreen>
      ) : payload ? (
        <AppScreen scroll>
          <ScreenHeader
            title="Activation QR"
            onBack={navigation.goBack}
          />
          <View style={styles.body}>
            <StatusBadge label={statusLabel} />
            {activated ? (
              <Text style={themed.success}>
                Device activated successfully. You can close this screen.
              </Text>
            ) : (
              <Text style={themed.body}>
                Scan this one-time QR on the kiosk app before it expires. The
                token can only be used once.
              </Text>
            )}

            {!activated ? (
              <ActivationQrCode value={payload.activationToken} />
            ) : null}

            <AppCard style={styles.card}>
              <Text style={themed.body}>
                Expires: {formatDateTime(payload.expiresAt)}
              </Text>
              <Text style={themed.token} selectable>
                {payload.activationToken}
              </Text>
              {copyMessage ? (
                <Text accessibilityRole="alert" style={themed.message}>
                  {copyMessage}
                </Text>
              ) : null}
            </AppCard>

            {!activated ? (
              <>
                <AppButton title="Copy token" onPress={onCopy} />
                <AppButton
                  title="Generate new QR"
                  variant="secondary"
                  onPress={generate}
                />
              </>
            ) : null}
            <AppButton
              title="Done"
              variant="text"
              onPress={() => navigation.goBack()}
            />
          </View>
        </AppScreen>
      ) : null}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  body: { marginTop: spacing.lg, gap: spacing.md },
  card: { gap: spacing.sm },
});
